import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';

function AdminDashboard() {
  const [serverStatus, setServerStatus] = useState({
    whatsapp: 'unknown',
    react: 'unknown'
  });
  const [stats, setStats] = useState({
    daily: { whatsapp: 0, gpt_requests: 0, gpt_tokens: 0 },
    weekly: { whatsapp: 0, gpt_requests: 0, gpt_tokens: 0 },
    monthly: { whatsapp: 0, gpt_requests: 0, gpt_tokens: 0 }
  });
  const [selectedPeriod, setSelectedPeriod] = useState('daily');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [monthStats, setMonthStats] = useState({ whatsapp: 0, gpt_requests: 0, gpt_tokens: 0 });
  const [loading, setLoading] = useState({
    react: false,
    whatsapp: false
  });
  const [notification, setNotification] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);

  useEffect(() => {
    // Tarayıcı title'ını ayarla
    document.title = 'FBAJANS WhatsApp Business - Admin Panel';
    
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 992);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // İlk yükleme
    loadDashboardStats();
    loadApplications();
    checkServerStatus();
    
    // Dropdown dışına tıklandığında kapat
    const handleClickOutside = (event) => {
      if (showMonthDropdown && !event.target.closest('[data-month-dropdown]')) {
        setShowMonthDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMonthDropdown]);

  const loadDashboardStats = async () => {
    try {
      const response = await fetch('/api/dashboard/stats');
      const data = await response.json();
      
      if (data) {
        setStats({
          daily: {
            whatsapp: data.daily?.whatsapp || 0,
            gpt_requests: data.daily?.gpt_requests || 0,
            gpt_tokens: data.daily?.gpt_tokens || 0
          },
          weekly: {
            whatsapp: data.weekly?.whatsapp || 0,
            gpt_requests: data.weekly?.gpt_requests || 0,
            gpt_tokens: data.weekly?.gpt_tokens || 0
          },
          monthly: {
            whatsapp: data.monthly?.whatsapp || 0,
            gpt_requests: data.monthly?.gpt_requests || 0,
            gpt_tokens: data.monthly?.gpt_tokens || 0
          }
        });
      }
    } catch (error) {
      console.error('Dashboard stats yükleme hatası:', error);
    }
  };

  // Geçmiş ayları oluştur (son 12 ay)
  const getPastMonths = () => {
    const months = [];
    const now = new Date();
    const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                       'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        monthName: monthNames[date.getMonth()],
        label: `${monthNames[date.getMonth()]} ${date.getFullYear()}`
      });
    }
    return months;
  };

  // Seçilen ay için istatistikleri yükle
  const loadMonthStats = async (year, month) => {
    try {
      const response = await fetch(`/api/dashboard/stats/month/${year}/${month}`);
      const data = await response.json();
      
      if (data) {
        setMonthStats({
          whatsapp: data.whatsapp || 0,
          gpt_requests: data.gpt_requests || 0,
          gpt_tokens: data.gpt_tokens || 0
        });
      }
    } catch (error) {
      console.error('Ay istatistikleri yükleme hatası:', error);
      setMonthStats({ whatsapp: 0, gpt_requests: 0, gpt_tokens: 0 });
    }
  };

  // Ay seçildiğinde
  const handleMonthSelect = (year, month, label) => {
    setSelectedMonth({ year, month, label });
    setShowMonthDropdown(false);
    setSelectedPeriod('monthly');
    loadMonthStats(year, month);
  };

  // Başvuruları yükle
  const loadApplications = async () => {
    try {
      setApplicationsLoading(true);
      const response = await fetch('/api/admin/applications');
      const data = await response.json();
      
      if (data.success) {
        // Sadece bekleyen başvuruları al
        const pendingApplications = (data.data || []).filter(app => app.status === 'pending');
        setApplications(pendingApplications);
      }
    } catch (error) {
      console.error('Başvurular yükleme hatası:', error);
    } finally {
      setApplicationsLoading(false);
    }
  };

  const checkServerStatus = async () => {
    try {
      // Check WhatsApp service through admin server
      const whatsappResponse = await fetch('/api/firms');
      setServerStatus(prev => ({
        ...prev,
        whatsapp: whatsappResponse.ok ? 'running' : 'stopped'
      }));
      
      // Check WhatsApp service status directly
      try {
        const statusResponse = await fetch('/api/whatsapp-service/status/fbajans');
        const statusData = await statusResponse.json();
        
        if (statusData.success) {
          // Update WhatsApp status based on actual service status
          const whatsappStatus = statusData.data.status || statusData.data.data?.status;
          console.log('WhatsApp Service Status:', whatsappStatus);
          
          // Update server status with actual WhatsApp status
          setServerStatus(prev => ({
            ...prev,
            whatsapp: whatsappStatus === 'connected' || whatsappStatus === 'authenticated' ? 'running' : 'stopped'
          }));
        }
      } catch (statusError) {
        console.error('WhatsApp service status check failed:', statusError);
        setServerStatus(prev => ({ ...prev, whatsapp: 'stopped' }));
      }
      
    } catch (error) {
      setServerStatus(prev => ({ ...prev, whatsapp: 'stopped' }));
    }
    setServerStatus(prev => ({ ...prev, react: 'running' }));
  };

  // WhatsApp durumu kontrol fonksiyonları
  const checkWhatsAppStatus = async () => {
    try {
      const response = await fetch('/api/whatsapp-service/status/fbajans');
      const data = await response.json();
      
      if (data.success) {
        const status = data.data.status;
        updateWhatsAppStatus(status);
      }
    } catch (error) {
      console.error('WhatsApp status hatası:', error);
    }
  };

  const updateWhatsAppStatus = (status) => {
    const qrSection = document.getElementById('whatsapp-qr-section');
    const qrImage = document.getElementById('qr-code-img');
    
    switch(status) {
      case 'qr_waiting':
        // QR kod göster
        qrImage.src = 'https://panel.fbajans.com/api/whatsapp-service/qr-image/fbajans';
        qrSection.style.display = 'block';
        break;
      case 'connected':
        // QR kodu gizle
        qrSection.style.display = 'none';
        break;
      case 'disconnected':
        // QR kodu gizle
        qrSection.style.display = 'none';
        break;
      default:
        // QR kodu gizle
        qrSection.style.display = 'none';
    }
  };

  // WhatsApp client başlatma
  const startWhatsAppClient = async () => {
    try {
      const response = await fetch('https://panel.fbajans.com/api/whatsapp-service/init/fbajans', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log('WhatsApp client başlatıldı:', data.data.status);
        // 2 saniye sonra durumu kontrol et
        setTimeout(() => {
          checkWhatsAppStatus();
        }, 2000);
      }
    } catch (error) {
      console.error('WhatsApp client başlatma hatası:', error);
    }
  };

  const loadStatistics = async () => {
    try {
      const periods = ['daily', 'weekly', 'monthly'];
      const newStats = {};
      
      for (const period of periods) {
        try {
          const response = await fetch(`/api/statistics/${period}`);
          const data = await response.json();
          
          if (data && Array.isArray(data)) {
            // Calculate totals from the data array
            const totals = data.reduce((acc, item) => ({
              whatsapp: acc.whatsapp + (item.message_count || 0),
              gpt_requests: acc.gpt_requests + (item.message_count || 0) * 0.5, // Estimate
              gpt_tokens: acc.gpt_tokens + (item.message_count || 0) * 25 // Estimate
            }), { whatsapp: 0, gpt_requests: 0, gpt_tokens: 0 });
            
            newStats[period] = totals;
          } else {
            throw new Error('API failed');
          }
        } catch (error) {
          newStats[period] = {
            whatsapp: period === 'daily' ? 45 : period === 'weekly' ? 312 : 1240,
            gpt_requests: period === 'daily' ? 23 : period === 'weekly' ? 156 : 620,
            gpt_tokens: period === 'daily' ? 1250 : period === 'weekly' ? 8430 : 34200
          };
        }
      }
      
      setStats(newStats);
    } catch (error) {
      console.error('İstatistik yükleme hatası:', error);
    }
  };

  const handleServerAction = async (service, action) => {
    setLoading(prev => ({ ...prev, [service]: true }));
    
    try {
      const response = await fetch('https://panel.fbajans.com/api/server_control.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service: service, action: action }),
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        setNotification({ type: 'success', message: data.message });
        setTimeout(() => checkServerStatus(), 3000);
      } else {
        throw new Error(data.message || 'İşlem başarısız');
      }
    } catch (error) {
      setNotification({
        type: 'error',
        message: error.message || 'Server işlemi başarısız'
      });
    } finally {
      setLoading(prev => ({ ...prev, [service]: false }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return '#28a745';
      case 'stopped': return '#dc3545';
      default: return '#ffc107';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'running': return 'Çalışıyor';
      case 'stopped': return 'Durduruldu';
      default: return 'Bilinmiyor';
    }
  };

  const formatNumber = (num) => {
    // Tam sayı göster, binlik ayırıcı ile (1.235 gibi)
    return num.toLocaleString('tr-TR');
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    // Gelecek tarihler için
    if (diff < 0) {
      return 'Gelecek';
    }
    
    // Saniye cinsinden fark
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) {
      return 'Az önce';
    }
    
    // Dakika cinsinden fark
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes} dakika önce`;
    }
    
    // Saat cinsinden fark
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours} saat önce`;
    }
    
    // Gün cinsinden fark
    const days = Math.floor(hours / 24);
    if (days < 7) {
      return `${days} gün önce`;
    }
    
    // Hafta cinsinden fark
    const weeks = Math.floor(days / 7);
    if (weeks < 4) {
      return `${weeks} hafta önce`;
    }
    
    // Ay cinsinden fark
    const months = Math.floor(days / 30);
    if (months < 12) {
      return `${months} ay önce`;
    }
    
    // Yıl cinsinden fark
    const years = Math.floor(days / 365);
    return `${years} yıl önce`;
  };

  const currentStats = selectedMonth && selectedPeriod === 'monthly' 
    ? monthStats 
    : stats[selectedPeriod];

  return (
    <AdminLayout>
      <div style={{ padding: '25px', backgroundColor: '#f8f9fa' }}>
        
        {/* Notification */}
        {notification && (
          <div style={{
            position: 'fixed',
            top: '100px',
            right: '20px',
            padding: '12px 20px',
            borderRadius: '8px',
            color: 'white',
            background: notification.type === 'success' ? '#28a745' : '#dc3545',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            animation: 'slideIn 0.3s ease'
          }}>
            {notification.message}
            <button 
              onClick={() => setNotification(null)}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: 'white', 
                marginLeft: '10px', 
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Standart Header */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '30px',
          marginBottom: '30px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '20px' : '0'
          }}>
            <div>
              <h1 style={{ 
                margin: '0 0 5px', 
                fontSize: isMobile ? '24px' : '32px', 
                fontWeight: '700',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                FBAJANS WhatsApp Business
              </h1>
              <p style={{ 
                margin: '0 0 5px', 
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: '500',
                color: '#555'
              }}>
                <i className="fas fa-chart-bar" style={{ marginRight: '8px' }}></i>
                Sistem Durumu
              </p>
              <p style={{ 
                margin: '5px 0 0 0', 
                fontSize: isMobile ? '13px' : '14px',
                color: '#999'
              }}>
                Son güncelleme: {new Date().toLocaleString('tr-TR')}
              </p>
            </div>
            <div style={{
              display: 'flex',
              gap: '15px',
              flexWrap: 'wrap',
              justifyContent: isMobile ? 'center' : 'flex-end'
            }}>
              {/* Period Selector */}
              <div style={{ 
                background: 'white', 
                padding: '5px', 
                borderRadius: '25px', 
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                display: 'flex',
                gap: '5px',
                alignItems: 'center'
              }}>
                {['daily', 'weekly', 'monthly'].map(period => (
                  <button
                    key={period}
                    onClick={() => {
                      setSelectedPeriod(period);
                      if (period !== 'monthly') {
                        setSelectedMonth(null);
                      }
                    }}
                    style={{
                      padding: '8px 20px',
                      border: 'none',
                      background: selectedPeriod === period && !selectedMonth ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                      color: selectedPeriod === period && !selectedMonth ? 'white' : '#666',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {period === 'daily' ? 'Bugün' : period === 'weekly' ? 'Son 7 Gün' : 'Son 30 Gün'}
                  </button>
                ))}
                
                {/* Aylar Dropdown */}
                <div style={{ position: 'relative' }} data-month-dropdown>
                  <button
                    onClick={() => {
                      setShowMonthDropdown(!showMonthDropdown);
                      if (!selectedMonth) {
                        setSelectedPeriod('monthly');
                      }
                    }}
                    style={{
                      padding: '8px 20px',
                      border: 'none',
                      background: selectedMonth ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                      color: selectedMonth ? 'white' : '#666',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    Aylar
                    <i className="fas fa-chevron-down" style={{ fontSize: '10px' }}></i>
                  </button>
                  
                  {showMonthDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '5px',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      minWidth: '200px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      padding: '5px'
                    }}>
                      {getPastMonths().map((monthItem, index) => (
                        <button
                          key={index}
                          onClick={() => handleMonthSelect(monthItem.year, monthItem.month, monthItem.label)}
                          style={{
                            width: '100%',
                            padding: '10px 15px',
                            border: 'none',
                            background: selectedMonth && selectedMonth.year === monthItem.year && selectedMonth.month === monthItem.month
                              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                              : 'transparent',
                            color: selectedMonth && selectedMonth.year === monthItem.year && selectedMonth.month === monthItem.month
                              ? 'white'
                              : '#666',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            textAlign: 'left',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!(selectedMonth && selectedMonth.year === monthItem.year && selectedMonth.month === monthItem.month)) {
                              e.target.style.background = '#f5f5f5';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!(selectedMonth && selectedMonth.year === monthItem.year && selectedMonth.month === monthItem.month)) {
                              e.target.style.background = 'transparent';
                            }
                          }}
                        >
                          {monthItem.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div style={{ 
                background: 'white', 
                padding: '5px', 
                borderRadius: '25px', 
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                display: 'flex',
                gap: '5px'
              }}>
                <button
                  onClick={checkServerStatus}
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #6f42c1, #5a2d91)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <i className="fas fa-sync-alt"></i>
                  Durumları Yenile
                </button>
                <button
                  onClick={loadDashboardStats}
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #28a745, #20c997)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <i className="fas fa-chart-bar"></i>
                  İstatistikleri Güncelle
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
          <div style={{ 
            display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))',
          gap: '15px',
          marginBottom: '25px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
          }}>
            <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '5px' }}>WhatsApp Mesajları</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{formatNumber(currentStats.whatsapp)}</div>
            <div style={{ fontSize: '11px', opacity: 0.7 }}>
              {selectedMonth 
                ? selectedMonth.label 
                : selectedPeriod === 'daily' 
                  ? 'Bugün' 
                  : selectedPeriod === 'weekly' 
                    ? 'Son 7 Gün' 
                    : 'Son 30 Gün'}
            </div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(245, 87, 108, 0.3)'
          }}>
            <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '5px' }}>GPT İstekleri</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{formatNumber(currentStats.gpt_requests)}</div>
            <div style={{ fontSize: '11px', opacity: 0.7 }}>
              {selectedMonth 
                ? selectedMonth.label 
                : selectedPeriod === 'daily' 
                  ? 'Bugün' 
                  : selectedPeriod === 'weekly' 
                    ? 'Son 7 Gün' 
                    : 'Son 30 Gün'}
            </div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(79, 172, 254, 0.3)'
          }}>
            <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '5px' }}>Token Kullanımı</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{formatNumber(currentStats.gpt_tokens)}</div>
            <div style={{ fontSize: '11px', opacity: 0.7 }}>
              {selectedMonth 
                ? selectedMonth.label 
                : selectedPeriod === 'daily' 
                  ? 'Bugün' 
                  : selectedPeriod === 'weekly' 
                    ? 'Son 7 Gün' 
                    : 'Son 30 Gün'}
            </div>
          </div>
            <div style={{
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white',
              padding: '20px',
              borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(250, 112, 154, 0.3)'
          }}>
            <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '5px' }}>Sistem Uptime</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>99.8%</div>
            <div style={{ fontSize: '11px', opacity: 0.7 }}>Son 30 gün</div>
          </div>
        </div>



        {/* Services Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(12, minmax(0, 1fr))',
          gap: '16px',
          marginBottom: '25px'
        }}>
          {/* WhatsApp Service */}
          <div style={{
            background: 'white',
            padding: '25px',
            borderRadius: '15px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0',
            gridColumn: isMobile ? 'auto' : 'span 3'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #25D366, #128C7E)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px'
                }}>
                  <i className="fas fa-mobile-alt" style={{ fontSize: '24px', color: 'white' }}></i>
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>WhatsApp Service</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Port: 3003</p>
                </div>
              </div>
              <div style={{
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                background: serverStatus.whatsapp === 'running' ? '#d4edda' : '#f8d7da',
                color: serverStatus.whatsapp === 'running' ? '#155724' : '#721c24'
                }}>
                  {getStatusText(serverStatus.whatsapp)}
              </div>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
              gap: '12px'
            }}>
                <button
                onClick={() => handleServerAction('whatsapp', 'restart')}
                  disabled={loading.whatsapp}
                  style={{
                    padding: '8px 16px',
                  background: loading.whatsapp ? '#6c757d' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: loading.whatsapp ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  flex: 1,
                  width: '100%',
                    opacity: loading.whatsapp ? 0.7 : 1
                  }}
              >
                {loading.whatsapp ? (
                  <>
                    <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                    İşleniyor...
                  </>
                ) : (
                  <>
                    <i className="fas fa-redo" style={{ marginRight: '8px' }}></i>
                    Yeniden Başlat
                  </>
                )}
                </button>
                <button
                  onClick={() => handleServerAction('whatsapp', 'stop')}
                disabled={loading.whatsapp}
                  style={{
                    padding: '8px 16px',
                  background: loading.whatsapp ? '#6c757d' : '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                  cursor: loading.whatsapp ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  flex: 1,
                  width: '100%',
                  opacity: loading.whatsapp ? 0.7 : 1
                }}
              >
                {loading.whatsapp ? '⏳ İşleniyor...' : '🛑 Durdur'}
                </button>
                <button
                  onClick={checkWhatsAppStatus}
                  style={{
                    padding: '8px 16px',
                    background: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    flex: 1,
                    width: '100%'
                  }}
                >
                  <i className="fas fa-mobile-alt" style={{ fontSize: '24px', color: '#25d366' }}></i> WhatsApp Durumu
                </button>
                <button
                  onClick={startWhatsAppClient}
                  style={{
                    padding: '8px 16px',
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    flex: 1,
                    width: '100%'
                  }}
                >
                  <i className="fas fa-rocket" style={{ marginRight: '8px' }}></i>
                  WhatsApp Başlat
                </button>
              </div>
              
              {/* WhatsApp QR Kod Bölümü */}
              <div id="whatsapp-qr-section" style={{ 
                marginTop: '15px', 
                padding: '15px', 
                border: '1px solid #e9ecef', 
                borderRadius: '8px',
                display: 'none'
              }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>
                  <i className="fas fa-mobile-alt" style={{ marginRight: '8px' }}></i>
                  WhatsApp QR Kod
                </h4>
                <div id="whatsapp-qr-image" style={{ textAlign: 'center' }}>
                  <img 
                    id="qr-code-img"
                    src="" 
                    alt="WhatsApp QR Kod" 
                    style={{ 
                      maxWidth: '200px', 
                      border: '1px solid #ddd',
                      borderRadius: '8px'
                    }}
                  />
                  <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#666' }}>
                    QR kodu telefonla okutun
                  </p>
                </div>
              </div>
            </div>

          {/* React Frontend */}
          <div style={{
            background: 'white',
            padding: '25px',
            borderRadius: '15px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0',
            gridColumn: isMobile ? 'auto' : 'span 3'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #61DAFB, #21D4FD)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px'
                }}>
                  <i className="fab fa-react" style={{ fontSize: '24px', color: 'white' }}></i>
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>React Frontend</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Port: 3000</p>
                </div>
              </div>
              <div style={{
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                background: serverStatus.react === 'running' ? '#d4edda' : '#f8d7da',
                color: serverStatus.react === 'running' ? '#155724' : '#721c24'
                }}>
                  {getStatusText(serverStatus.react)}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px' }}>
                <button
                onClick={() => handleServerAction('react', 'restart')}
                  disabled={loading.react}
                  style={{
                    padding: isMobile ? '10px' : '8px 16px',
                  background: loading.react ? '#6c757d' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: loading.react ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '13px' : '12px',
                  fontWeight: '600',
                  width: isMobile ? '100%' : 'auto',
                    opacity: loading.react ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    flex: 1
                  }}
              >
                {loading.react ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    İşleniyor...
                  </>
                ) : (
                  <>
                    <i className="fas fa-redo"></i>
                    Yeniden Başlat
                  </>
                )}
                </button>
                <button
                  onClick={() => handleServerAction('react', 'stop')}
                disabled={loading.react}
                  style={{
                    padding: isMobile ? '10px' : '8px 16px',
                  background: loading.react ? '#6c757d' : '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                  cursor: loading.react ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '13px' : '12px',
                  fontWeight: '600',
                  width: isMobile ? '100%' : 'auto',
                  opacity: loading.react ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  flex: 1
                }}
              >
                {loading.react ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    İşleniyor...
                  </>
                ) : (
                  <>
                    <i className="fas fa-stop-circle"></i>
                    Durdur
                  </>
                )}
                </button>
            </div>
          </div>
          <div style={{
            background: 'white',
            padding: '25px',
            borderRadius: '16px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '1px solid #e2e8f0',
            gridColumn: isMobile ? 'auto' : 'span 6',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
            minHeight: '100%'
          }}>
            {applications.length > 0 ? (
              <>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: '12px'
            }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '12px',
                  background: 'linear-gradient(135deg, #ffc107, #fd7e14)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                      color: 'white'
                }}>
                      <i className="fas fa-user-clock"></i>
                </div>
                <div>
                      <h3 style={{ margin: 0, fontSize: isMobile ? '18px' : '22px', color: '#333' }}>
                    Bekleyen Başvurular
                  </h3>
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>
                        {applications.length} başvuru inceleme bekliyor
                  </p>
                </div>
              </div>
              <button
                onClick={() => window.location.href = '/admin/applications'}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                      padding: '10px 18px',
                      borderRadius: '999px',
                  cursor: 'pointer',
                      fontSize: '13px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <i className="fas fa-eye"></i>
                Tümünü Gör
              </button>
            </div>

            <div style={{
              display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))',
                  gap: '12px'
            }}>
              {applications.slice(0, 3).map((application) => (
                <div key={application.id} style={{
                  background: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '12px',
                      padding: '16px',
                  display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <strong style={{ fontSize: '16px', color: '#333' }}>{application.fullName}</strong>
                          <span style={{ fontSize: '13px', color: '#666' }}>{application.email}</span>
                          <span style={{ fontSize: '13px', color: '#666' }}>{application.whatsappNumber}</span>
                        </div>
                        <span style={{
                    background: '#ffc107',
                    color: 'white',
                          padding: '4px 10px',
                          borderRadius: '999px',
                    fontSize: '11px',
                          fontWeight: '600',
                          alignSelf: 'flex-start'
                  }}>
                    Bekliyor
                        </span>
                  </div>
                  
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        <i className="far fa-clock" style={{ marginRight: '6px' }}></i>
                        {formatRelativeTime(application.createdAt)}
                  </div>

                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        fetch(`/api/admin/applications/${application.id}/approve`, {
                          method: 'POST'
                        }).then(() => {
                          loadApplications();
                          setNotification({
                            type: 'success',
                            message: 'Başvuru onaylandı!'
                          });
                        });
                      }}
                      style={{
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                            padding: '8px 14px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                            fontSize: '13px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                            flex: 1,
                            justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#218838';
                      }}
                      onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#28a745';
                      }}
                    >
                      <i className="fas fa-check"></i>
                      Onayla
                    </button>
                    <button
                      onClick={() => {
                        fetch(`/api/admin/applications/${application.id}/reject`, {
                          method: 'POST'
                        }).then(() => {
                          loadApplications();
                          setNotification({
                                type: 'warning',
                                message: 'Başvuru reddedildi.'
                          });
                        });
                      }}
                      style={{
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                            padding: '8px 14px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                            fontSize: '13px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                            flex: 1,
                            justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#c82333';
                      }}
                      onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#dc3545';
                      }}
                    >
                      <i className="fas fa-times"></i>
                      Reddet
                    </button>
                  </div>
                </div>
              ))}
            </div>
              </>
        ) : (
          <div style={{
                flex: 1,
            display: 'flex',
                flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
                gap: '12px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #28a745, #20c997)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 6px 18px rgba(40, 167, 69, 0.35)',
                  color: 'white',
                  fontSize: '26px'
                }}>
                  <i className="fas fa-check"></i>
            </div>
                <h3 style={{ margin: 0, fontSize: '20px', color: '#333' }}>Bekleyen Başvuru Yok</h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                  Şu anda işlenmemiş başvuru bulunmuyor
                </p>
          </div>
        )}
          </div>
        </div>

      </div>

      <style jsx>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </AdminLayout>
  );
}

export default AdminDashboard; 